// Course catalog documents in the aura-courses Firestore project:
//   courses/{id} -> lessons/{id} -> videos/{id}
// Moved from aura-courses-admin (course.types.ts). Field names and units are
// the stored ones: durations are whole MINUTES, totals are recomputed
// client-side after every lesson or video change (services/courses).

export const COURSE_STATUSES = ["draft", "approved", "published", "archived"] as const;
export type CourseStatus = (typeof COURSE_STATUSES)[number];

export interface MultipleChoiceQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswerIndex: number;
  explanation?: string;
}

// Stored inline on the lesson document.
export interface KnowledgeCheck {
  title: string;
  duration: number; // minutes
  questions: MultipleChoiceQuestion[];
  thumbnail?: string;
  locked?: boolean;
}

export interface Video {
  id?: string;
  title: string;
  youtubeUrl: string;
  duration: number; // minutes
  thumbnail?: string;
  order: number;
  completed?: boolean;
  transcript?: string; // markdown
  createdAt?: Date;
}

export interface Lesson {
  id?: string;
  title: string;
  description?: string;
  order: number;
  totalDuration?: number; // recomputed from the videos
  knowledgeCheck?: KnowledgeCheck;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Course {
  id?: string;
  title: string;
  description: string;
  imageUrl: string;
  status: CourseStatus;
  totalLessons?: number; // recomputed from the lessons subcollection
  totalDuration?: number; // recomputed from all videos and knowledge checks
  progress?: number; // 0-100, written by the mobile app
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  tags?: string[]; // written by the aura-course-rag Cloud Function
}

export interface CourseFormData {
  title: string;
  description: string;
  imageUrl: string;
  status: CourseStatus;
  tags?: string[];
}

export interface LessonFormData {
  title: string;
  description?: string;
  order: number;
  knowledgeCheck?: KnowledgeCheck;
}

export interface VideoFormData {
  title: string;
  youtubeUrl: string;
  duration: number;
  thumbnail?: string;
  order: number;
  transcript?: string;
}

export function emptyCourse(): Course {
  return { title: "", description: "", imageUrl: "", status: "draft" };
}

export function emptyKnowledgeCheck(): KnowledgeCheck {
  return { title: "Knowledge Check", duration: 5, questions: [] };
}

// "1 h 5 min" from whole minutes.
export function formatMinutes(minutes: number | undefined): string {
  const m = minutes || 0;
  if (!m) return "0 min";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h) return r ? `${h} h ${r} min` : `${h} h`;
  return `${m} min`;
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}
